import React from "react";
import { motion } from "framer-motion";

const PURPLE_GRADIENT = "linear-gradient(0.7deg, #A28FDC 0.74%, #7C3AED 74.61%)";

const SevenDayCard = ({ car, onBook }) => {
  const rentPerDay     = car.rentPerDay != null ? Number(car.rentPerDay) : null;
  const rawCost        = car.costToCustomer ?? car.costToTheCustomer;
  const costToCustomer = rawCost != null ? Number(rawCost) : null;
  const allottedKm    = car.totalFreeKms;

  return (
    <motion.article
      layout
      className={`relative bg-white border border-[#E5E7EB] rounded-xl shadow-[0px_8px_26px_-11px_rgba(212,184,184,0.07)] flex flex-col gap-6 p-4 overflow-hidden transition-all ${car.isSoldOut ? "opacity-60" : ""}`}
    >
      {/* Red diagonal "Short Subscription" banner — anchored top-right corner */}
      <div
        className="absolute pointer-events-none"
        style={{ right: -61, top: -32, width: 208, height: 127 }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <div style={{ transform: "rotate(26.34deg)" }}>
            <div
              className="flex items-center justify-center"
              style={{ background: "#E91D21", width: 215, height: 35, filter: "drop-shadow(0px 2px 5px rgba(0,0,0,0.12))" }}
            >
              <span className="font-['Outfit'] font-medium text-white text-[14px] text-center leading-[14px] tracking-[0.14px] w-[105px]">
                Short Subscription
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top row: details left + image right */}
      <div className="flex items-start justify-between">
        {/* Left: details */}
        <div className="flex flex-col gap-[14.7px] items-start shrink-0">
          {/* Name + year + body type */}
          <div className="flex flex-col gap-[7.4px] items-start">
            <span className="text-[#252525] text-[22px] font-semibold font-['Outfit'] leading-[1.2] tracking-[0.4px]">
              {car.name}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              {car.modelYear && (
                <span className="text-[#374151] text-[12.9px] font-medium font-['Outfit']">{car.modelYear}</span>
              )}
              {car.modelYear && car.bodyTypeText && (
                <span className="text-[#374151] text-[12.9px] font-medium font-['Outfit']">|</span>
              )}
              {car.bodyTypeText && (
                <span className="px-2 py-0.5 bg-[#F9FAFB] border border-[#F3F3F9] rounded text-[#1D4ED8] text-xs font-semibold font-['Outfit'] uppercase tracking-[1px]">
                  {car.bodyTypeText}
                </span>
              )}
            </div>
          </div>

          {/* Specs pills */}
          <div className="flex items-center gap-[7.4px] min-[1880px]:gap-[5.2px] flex-wrap">
            <span className="px-[7.4px] py-[7.4px] min-[1880px]:px-[5.2px] min-[1880px]:py-[5.2px] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[7.4px] min-[1880px]:rounded-[5.2px] text-[#111827] text-[12.9px] min-[1880px]:text-[9px] font-semibold font-['Outfit'] whitespace-nowrap">
              {car.seater} Seats
            </span>
            <span className="px-[7.4px] py-[7.4px] min-[1880px]:px-[5.2px] min-[1880px]:py-[5.2px] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[7.4px] min-[1880px]:rounded-[5.2px] text-[#111827] text-[12.9px] min-[1880px]:text-[9px] font-semibold font-['Outfit'] whitespace-nowrap">
              {car.transmissionTypeText}
            </span>
            <span className="px-[7.4px] py-[7.4px] min-[1880px]:px-[5.2px] min-[1880px]:py-[5.2px] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[7.4px] min-[1880px]:rounded-[5.2px] text-[#111827] text-[12.9px] min-[1880px]:text-[9px] font-semibold font-['Outfit'] whitespace-nowrap">
              {car.fuelTypeText}
            </span>
          </div>

          {/* Pricing */}
          <div className="flex flex-col gap-[5.5px] items-start">
            <span className="text-[#646464] text-[11px] font-medium font-['Outfit'] tracking-[0.22px]">
              Starting from
            </span>
            {/* Main price: rentPerDay (coming soon from API) */}
            {rentPerDay != null ? (
              <div className="flex items-end gap-1">
                <span className="text-[#CD2525] text-[29.5px] font-bold font-['Outfit'] tracking-[-0.29px] leading-none">
                  ₹{rentPerDay.toLocaleString("en-IN")}
                </span>
                <span className="text-[#CD2525] text-[14px] font-semibold font-['Outfit'] mb-[3px]">/day</span>
              </div>
            ) : (
              <div className="h-[36px]" /> /* placeholder until API field is live */
            )}
            <p className="text-[#6B7280] text-[11px] font-medium font-['Outfit'] tracking-[0.22px] text-left">
              {car.baseFare != null && car.taxAmount != null && (
                <span>₹{(Number(car.baseFare) + Number(car.taxAmount)).toLocaleString("en-IN")} · </span>
              )}
              Incl. Taxes &amp; Excl. Fees
            </p>
          </div>
        </div>

        {/* Right: car image */}
        <div className="relative shrink-0 w-[309px] h-[218px] min-[1880px]:w-[216px] min-[1880px]:h-[153px]">
          <img
            src={car.imageUrl || "/assets/sample.png"}
            alt={car.name}
            className="absolute bottom-0 left-0 w-full h-[181px] min-[1880px]:h-[127px] object-contain"
          />
        </div>
      </div>

      {/* Bottom row: allotted km + Book Now */}
      <div className="flex items-center justify-between">
        <div className="flex items-center justify-center gap-2 border border-[#9B80DF] rounded-[6px] px-3 h-[36px] w-[218px]">
          <span className="text-[#404040] text-[14px] font-semibold font-['Outfit'] tracking-[0.28px]">
            Allotted KM :
          </span>
          <span className="text-[#300F80] text-[16px] font-bold font-['Outfit']">
            {allottedKm ? `${allottedKm}KM` : "—"}
          </span>
        </div>
        <button
          disabled={car.isSoldOut}
          onClick={() => onBook(car)}
          className={`h-8 px-4 rounded-lg text-[15.2px] font-medium font-['Outfit'] tracking-[0.43px] text-white border whitespace-nowrap ${car.isSoldOut ? "bg-gray-200 text-gray-400 border-gray-200" : "border-[#7C3AED] shadow-[0px_10.84px_13px_rgba(124,58,237,0.2)]"}`}
          style={!car.isSoldOut ? { backgroundImage: PURPLE_GRADIENT } : undefined}
        >
          {car.isSoldOut ? "Sold Out" : "Book Now"}
        </button>
      </div>
    </motion.article>
  );
};

export default SevenDayCard;
